/**
 * Primitive Representations
 *
 * These are foundational, reusable representations that can be referenced
 * by domain-specific representations. They represent the basic mathematical
 * types that appear across many domains.
 *
 * Usage:
 * - Import the model for type consistency
 * - Reference via `base_representation_id` in domain-specific representations
 * - Or import directly if no customization is needed
 */

import { field } from '../../field-types';
import { defineRepresentation, RepresentationCategory } from '../../ontology.model';

// ------------------------------------------------
// PRIMITIVE MODELS
// ------------------------------------------------

export const RealNumberModel = {
  value: field.number({
    description: 'A real number value',
    example: 42,
  }),
} as const;

export const IntegerModel = {
  value: field.integer({
    description: 'An integer value',
    example: 42,
  }),
} as const;

export const NaturalNumberModel = {
  value: field.natural({
    description: 'A natural number (positive integer)',
    example: 5,
  }),
} as const;

export const FractionModel = {
  numerator: field.integer({
    description: 'The numerator of the fraction',
    example: 3,
  }),
  denominator: field.integer({
    description: 'The denominator of the fraction (non-zero)',
    example: 4,
    min: 1,
  }),
  value: field.number({
    description: 'The decimal value of the fraction',
    example: 0.75,
    optional: true,
  }),
} as const;

export const PercentageModel = {
  value: field.number({
    description: 'The percentage value (0-100)',
    example: 75,
    min: 0,
    max: 100,
  }),
} as const;

export const BooleanStateModel = {
  value: field.boolean({
    description: 'A true/false value',
    example: true,
  }),
} as const;

// ------------------------------------------------
// PRIMITIVE REPRESENTATIONS
// ------------------------------------------------

export const primitiveRepresentations = [
  defineRepresentation({
    id: 'rep.prim.real',
    name: 'Real Number',
    tag_ids: ['tag.num'],
    description: 'A real number - the fundamental numeric type. Can be any decimal value.',
    category: RepresentationCategory.Value,
    type: 'numerical',
    model: RealNumberModel,
    template: '{{value}}',
    example_data: { value: 42 },
    example: '$42$, $3.14159$, $-7.5$',
  }),

  defineRepresentation({
    id: 'rep.prim.integer',
    name: 'Integer',
    tag_ids: ['tag.num'],
    description: 'A whole number (positive, negative, or zero). No fractional part.',
    category: RepresentationCategory.Value,
    type: 'numerical',
    model: IntegerModel,
    template: '{{value}}',
    example_data: { value: 42 },
    example: '$-3$, $0$, $42$',
  }),

  defineRepresentation({
    id: 'rep.prim.natural',
    name: 'Natural Number',
    tag_ids: ['tag.num'],
    description: 'A positive integer starting from 1. Used for counting.',
    category: RepresentationCategory.Value,
    type: 'numerical',
    model: NaturalNumberModel,
    template: '{{value}}',
    example_data: { value: 5 },
    example: '$1$, $2$, $3$, $\\ldots$',
  }),

  defineRepresentation({
    id: 'rep.prim.fraction',
    name: 'Fraction',
    tag_ids: ['tag.num'],
    description: 'A ratio of two integers. Represents exact division.',
    category: RepresentationCategory.Form,
    type: 'numerical',
    model: FractionModel,
    template: '\\frac{{{numerator}}}{{{denominator}}}',
    example_data: { numerator: 3, denominator: 4, value: 0.75 },
    example: '$\\frac{3}{4}$, $\\frac{1}{2}$, $\\frac{22}{7}$',
  }),

  defineRepresentation({
    id: 'rep.prim.percentage',
    name: 'Percentage',
    tag_ids: ['tag.num'],
    description: 'A number expressed as a fraction of 100. Common in statistics and everyday use.',
    category: RepresentationCategory.Form,
    type: 'numerical',
    model: PercentageModel,
    template: '{{value}}\\%',
    example_data: { value: 75 },
    example: '$75\\%$, $100\\%$, $33.3\\%$',
  }),
];

// Model registry for type-safe procedures
export const primitiveModels = {
  'rep.prim.real': RealNumberModel,
  'rep.prim.integer': IntegerModel,
  'rep.prim.natural': NaturalNumberModel,
  'rep.prim.fraction': FractionModel,
  'rep.prim.percentage': PercentageModel,
} as const;
